import { useMemo, useState } from "react";
import { Badge } from "@/components/base/badges/badges";
import { DataTable, type Column } from "@/components/application/data-table/data-table";

/**
 * UC-001 SyncHistoryTable (Phase 13) — Data Table per-zone sync_run history.
 *
 * Per princip #4 (sync transparency, REQ-UI-024):
 *   - Filter chips per stav (vše, ok, částečný, selhal, běží).
 *   - StatusBadge color-not-only — text + barva (Badge variant).
 *   - Drift indicator (UC-001 Krok 2): castecny řádek má dodatečný "drift"
 *     marker pro princip #4 transparency.
 *   - Rows clickable pro detail (rezervováno pozdější Phase pro drill-down).
 */

export interface SyncHistoryRow {
  sync_run_id: string;
  zone_id: string;
  zone_code: string;
  state: string;
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  inventory_count: number | null;
  drift_indicator: boolean;
}

export interface SyncHistoryTableProps {
  rows: SyncHistoryRow[];
  loading?: boolean;
  onRowClick?: (row: SyncHistoryRow) => void;
}

const FILTER_CHIPS: { id: string; label: string }[] = [
  { id: "all", label: "Vše" },
  { id: "kompletni", label: "OK" },
  { id: "castecny", label: "Částečný" },
  { id: "selhal", label: "Selhal" },
  { id: "running", label: "Běží" },
];

function colorForState(state: string): "success" | "warning" | "error" | "blue" | "gray" {
  switch (state) {
    case "kompletni":
      return "success";
    case "castecny":
      return "warning";
    case "selhal":
      return "error";
    case "running":
      return "blue";
    default:
      return "gray";
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} min ${s} s`;
}

// Filter chip (pill) styling — UUI utility překlad z legacy .sync-history__chip.
const CHIP_BASE =
  "min-h-touch rounded-full border px-4 py-2 text-sm cursor-pointer outline-focus-ring focus-visible:ring-2 focus-visible:ring-brand";
const CHIP_INACTIVE = "bg-transparent border-secondary text-primary";
const CHIP_ACTIVE = "bg-brand-solid border-brand text-white";

function chipClass(isActive: boolean): string {
  return `${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function SyncHistoryTable({ rows, loading, onRowClick }: SyncHistoryTableProps) {
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [zoneFilter, setZoneFilter] = useState<string>("all");

  // Střediska přítomná v datech → dynamické filter chipy (vzestupně S0/S1/S2…).
  const zoneCodes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.zone_code))).sort(),
    [rows],
  );

  const filteredRows = useMemo(() => {
    return rows
      .filter((r) => activeFilter === "all" || r.state === activeFilter)
      .filter((r) => zoneFilter === "all" || r.zone_code === zoneFilter)
      // Primární řazení: doba zahájení sestupně (nejnovější nahoře).
      .slice()
      .sort((a, b) => b.started_at.localeCompare(a.started_at));
  }, [rows, activeFilter, zoneFilter]);

  const columns: Column<SyncHistoryRow>[] = [
    {
      key: "zone",
      header: "Zóna",
      cell: (r) => <strong>{r.zone_code}</strong>,
    },
    {
      key: "state",
      header: "Stav",
      cell: (r) => (
        <span className="inline-flex items-center gap-2">
          <Badge color={colorForState(r.state)}>{r.state}</Badge>
          {r.drift_indicator && (
            <span
              className="text-xs font-bold text-warning-primary"
              data-testid="sync-drift-indicator"
            >
              <span aria-hidden="true">⚠</span> drift
            </span>
          )}
        </span>
      ),
    },
    {
      key: "started",
      header: "Zahájeno",
      cell: (r) => <time dateTime={r.started_at}>{formatDate(r.started_at)}</time>,
    },
    {
      key: "duration",
      header: "Doba běhu",
      cell: (r) => formatDuration(r.duration_seconds),
    },
    {
      key: "inventory",
      header: "Dokumentů",
      cell: (r) => (r.inventory_count != null ? r.inventory_count : "—"),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div
        className="flex flex-wrap gap-2"
        role="group"
        aria-label="Filtr stavů synchronizace"
      >
        {FILTER_CHIPS.map((chip) => {
          const isActive = activeFilter === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              className={chipClass(isActive)}
              aria-pressed={isActive}
              onClick={() => setActiveFilter(chip.id)}
              data-testid={`sync-filter-${chip.id}`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
      {zoneCodes.length > 1 && (
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="Filtr středisek"
        >
          {[{ id: "all", label: "Všechna střediska" }, ...zoneCodes.map((z) => ({ id: z, label: z }))].map(
            (chip) => {
              const isActive = zoneFilter === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  className={chipClass(isActive)}
                  aria-pressed={isActive}
                  onClick={() => setZoneFilter(chip.id)}
                  data-testid={`sync-zone-filter-${chip.id}`}
                >
                  {chip.label}
                </button>
              );
            },
          )}
        </div>
      )}
      <DataTable
        caption="Historie synchronizací (posledních 50 záznamů)"
        captionVisible={false}
        columns={columns}
        rows={filteredRows}
        rowKey={(r) => r.sync_run_id}
        emptyMessage="Žádné záznamy odpovídající filtru."
        loading={loading}
        onRowClick={onRowClick}
      />
    </div>
  );
}
